import {WorkAcceptance} from "../components/WorkAcceptance";
import {useSavedReport} from "../hooks/useReads";
// The work order: the platform's WO in the signed language. Site (Open in Maps,
// parking & unloading, access, PPE chips, office contact) · notes from the office
// · materials on site · your labour (external only) · set-out drawing · days with
// "Day complete" per scheduled day · one photo gallery · scope by room.
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, MapPin, Phone } from "lucide-react";
import { Button } from "@aiw/ui";
import { ppeWord, useT } from "../i18n";
import { usePageHead } from "../shell/Frame";
import { useRole } from "../hooks/useMe";
import { pickCurrent, useJob, useJobInstalls, useJobSkirting, useMyJobs, useWorkOrder } from "../hooks/useReads";
import { mapsLink } from "../lib/api";
import { aud, day, dayRange, isPast, isSameDay, m2, time } from "../lib/format";
import { useReport } from "../state/reports";
import { Chip, KV, Panel, Pill } from "../components/Panel";
import { PhotoButtons, PhotoGrid } from "../components/PhotoGallery";
import { SetOutDrawing } from "../components/SetOutDrawing";

export function WorkOrder() {
  const { ref } = useParams();
  const jobs = useMyJobs();
  if (!ref) {
    const cur = pickCurrent(jobs.data);
    if (jobs.isLoading) return <Loading />;
    return cur ? <Navigate to={`/work-order/${cur.ref}`} replace /> : <NoJob />;
  }
  return <WorkOrderFor jobRef={ref} />;
}

function Loading() { const { t } = useT(); return <p className="text-sm text-muted-foreground">{t("loading")}</p>; }
function NoJob() { const { t } = useT(); usePageHead(t("tab_wo")); return <p className="text-sm text-muted-foreground">{t("wo_no_job")}</p>; }

function WorkOrderFor({ jobRef }: { jobRef: string }) {
  const { t, lang } = useT();
  const { isExternal, isInternal, canInstall, partyName } = useRole();
  const job = useJob(jobRef);
  const installs = useJobInstalls(jobRef);
  const wo = useWorkOrder(jobRef);
  const skirting=useJobSkirting(jobRef);
  const report = useReport(jobRef);
  const install = installs.data?.[0] ?? null;
  const installationId = job.data?.installation_id ?? installs.data?.[0]?.id;
  const saved = useSavedReport(jobRef, installationId);
  const days = install?.days ?? [];
  const openDays = days.filter((d) => !(saved.data?.days_done?.[d.id??""] ?? d.done));
  const focusDay = openDays.find((d) => isSameDay(d.date))
    ?? openDays.find((d) => isPast(d.date))
    ?? openDays[0] ?? null;

  usePageHead(t("wo_title", { name: job.data?.name ?? jobRef }), job.data?.address ?? job.data?.suburb ?? null);

  if (job.isLoading) return <Loading />;
  if (job.isError || installs.isError) return <Panel><p role="alert" className="text-sm text-destructive">{t("work_load_failed")}</p><Button size="touch" className="mt-3" onClick={() => { void job.refetch(); void installs.refetch(); }}>{t("retry_work")}</Button></Panel>;
  if (!job.data) return <p className="text-sm text-muted-foreground">{t("wo_no_job")}</p>;

  const j = job.data;
  const w = wo.data ?? null;
  const address = w?.address ?? j.address ?? "";
  const first = days[0]?.date ?? j.install_next_date;
  const last = days[days.length - 1]?.date ?? null;

  return (
    <div className="grid gap-4 md:grid-cols-2 md:items-start">
      <div className="space-y-4">
        <WorkAcceptance jobRef={jobRef} installation={installationId}/>
        {!canInstall && <p className="rounded-md border px-3 py-2 text-sm text-muted-foreground">{t("onsite_access_needed")}</p>}
        {focusDay && <Panel title={t("assigned_day")}>
          <p className="text-sm font-semibold">{day(focusDay.date, lang)}</p>
          <p className="mt-2 text-sm">{focusDay.task?.trim() || t("scope_missing")}</p>
          {canInstall && (isSameDay(focusDay.date) || isPast(focusDay.date)) && focusDay.task?.trim() &&
            <Button asChild size="touch" className="mt-3 w-full"><Link to={`/work-order/${jobRef}/completion?day=${encodeURIComponent(focusDay.id??"")}`}>{t("btn_day_complete")}</Link></Button>}
        </Panel>}
        <Link className="flex min-h-11 items-center justify-center rounded-md border bg-card px-4 text-sm font-semibold text-primary" to={`/work-order/${jobRef}/site-record`}>{t("site_record_open")}</Link>
        <Link className="flex min-h-11 items-center justify-center rounded-md border bg-card px-4 text-sm font-semibold text-primary" to={`/work-order/${jobRef}/lay-diagram`}>{t("lay_open")}</Link>
        {saved.data&&!saved.data.schedule_ready&&<p className="text-sm text-warning">{t("recorded_day_required")}</p>}
        {!w && !wo.isLoading && <p role={wo.isError ? "alert" : undefined} className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">{t(wo.isError ? "work_load_failed" : "wo_not_published")}{wo.isError && <Button size="touch" variant="secondary" className="mt-2" onClick={() => void wo.refetch()}>{t("retry_work")}</Button>}</p>}

        <Panel title={t("site")} right={w && <Chip mono>{w.wo_ref}</Chip>}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">{address}</p>
              <p className="text-micro text-muted-foreground">{w?.customer ?? j.customer}{(w?.customer_phone ?? j.customer_phone) ? ` · ${w?.customer_phone ?? j.customer_phone}` : ""}</p>
            </div>
            {address && (
              <Button asChild size="touch" variant="secondary" className="shrink-0 px-3 text-sm">
                <a href={mapsLink(address)} target="_blank" rel="noreferrer"><MapPin aria-hidden />{t("open_maps")}</a>
              </Button>
            )}
          </div>
          <div className="mt-3">
            <KV rows={[
              { k: t("days"), v: `${dayRange(first, last, lang)} · ${w?.start_time ? time(w.start_time, lang) : t("start_time_unset")}` },
              ...(w?.parking ? [{ k: t("parking"), v: w.parking }] : []),
              ...(w?.access ? [{ k: t("access"), v: w.access }] : []),
              ...(w?.ppe.length ? [{ k: t("ppe"), v: <span className="flex flex-wrap gap-1.5">{w.ppe.map((p) => <Pill key={p} tone="warn">{ppeWord(lang, p)}</Pill>)}</span> }] : []),
              ...(w?.office_contact ? [{ k: t("office"), v: <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />{w.office_contact.name} · {w.office_contact.phone}</span> }] : []),
            ]} />
          </div>
        </Panel>

        {w?.notes && (
          <Panel title={t("notes_office")}>
            <p className="text-sm leading-relaxed">{w.notes}</p>
          </Panel>
        )}

        {!!w?.pickup_orders?.length && <Panel title={t("supplier_pickups")} bodyClassName="p-0">
          <ul className="divide-y">
            {w.pickup_orders.map((order) => <li key={order.order} className="px-4 py-3 text-sm">
              <p className="font-semibold">{order.supplier} · {order.order}</p>
              {order.pickup_references.length ? order.pickup_references.map(ref =>
                <p key={ref} className="mt-1"><span className="text-muted-foreground">{t("pickup_number")}: </span><b className="select-all text-base">{ref}</b></p>)
                : <p className="mt-1 text-warning">{t("pickup_awaiting_email")}</p>}
            </li>)}
          </ul>
        </Panel>}

        {w && (
          <Panel title={t("materials")} right={w.delivered_at ? <Pill tone="ok">{t("delivered", { date: day(w.delivered_at, lang) })}</Pill> : <Pill tone="plain">{t("materials_status_unrecorded")}</Pill>} bodyClassName="p-0">
            <ul className="divide-y">
              {w.materials.map((m) => (
                <li key={m.item} className="flex items-start justify-between gap-3 px-4 py-2.5 text-sm">
                  <span className="min-w-0"><span className="font-medium">{m.item}</span>{m.detail && <span className="block text-micro text-muted-foreground">{m.detail}</span>}</span>
                  <span className="num shrink-0 text-muted-foreground">{m.qty}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {isExternal && w?.labour && (
          <Panel title={t("your_labour")} right={<span>{t("party_only", { party: partyName })}</span>}>
            <KV rows={[
              ...w.labour.map((l) => ({ k: l.line, v: <span>{l.qty} · {l.rate} · <b className="num">{aud(l.amount)}</b></span> })),
              { k: t("labour_total"), v: <span><b className="num">{aud(w.labour.reduce((s, l) => s + l.amount, 0))}</b> · <span className="text-muted-foreground">{t("on_statement")}</span></span> },
            ]} />
          </Panel>
        )}
        {isInternal && <p className="rounded-lg border bg-card px-4 py-3 text-micro text-muted-foreground">{t("internal_no_rates")}</p>}
      </div>

      <div className="space-y-4">
        {skirting.data?.record&&<Panel title={t('skirting_scope')}>
          <p className="text-sm font-medium">{t(skirting.data.record.scope==='supply_install'?'skirting_supply_install':'skirting_install_only')}</p>
          {skirting.data.record.measured_net_lm===null?<p className="mt-2 text-sm text-warning">{t('skirting_measurement_pending')}</p>:<p className="mt-2 text-sm">{t('skirting_measured',{length:String(skirting.data.record.measured_net_lm)})}</p>}
          {skirting.data.record.scope==='supply_install'&&(skirting.data.record.profile_id?<p className="mt-2 text-sm">{skirting.data.record.profile_id} · {skirting.data.record.variant_id}</p>:<p className="mt-2 text-sm text-warning">{t('skirting_profile_pending')}</p>)}
        </Panel>}
        {w && (
          <Panel title={t("setout_drawing")} right={<span>{t("setout_caption", { date: day(w.measured_at, lang) })}</span>} bodyClassName="p-0">
            {w.plan ? <SetOutDrawing plan={w.plan} /> : <p className="p-4 text-sm">{t("drawing_not_published")}</p>}
          </Panel>
        )}

        <Panel title={t("days")} right={<span>{t("days_caption")}</span>} bodyClassName="p-0">
          <ul className="divide-y">
            {days.map((d, i) => {
              const done = saved.data?.days_done?.[d.id??""] ?? d.done;
              const today = isSameDay(d.date);
              const due = today || isPast(d.date);
              const assignedToThisInstaller=!d.assignee?.id||d.assignee.id===saved.data?.party_id;
              return (
                <li key={d.id??`${d.date}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{day(d.date, lang)} · {t("day_n", { n: i + 1 })}</p>
                    {d.task && <p className="text-micro text-muted-foreground">{d.task}</p>}
                  </div>
                  {done ? (
                    <Pill tone="ok"><Check className="mr-1 h-3.5 w-3.5" aria-hidden />{t("done_at", { time: time(done, lang) })}</Pill>
                  ) : due && assignedToThisInstaller && canInstall ? (
                    <Button asChild size="touch" className="shrink-0 px-3 text-sm"><Link to={`/work-order/${jobRef}/completion?day=${encodeURIComponent(d.id??"")}`}>{t("btn_day_complete")}</Link></Button>
                  ) : due ? (
                    <Pill tone="plain">{t('day_other_installer',{name:d.assignee?.name??d.crew??''})}</Pill>
                  ) : (
                    <Pill tone="plain">{isSameDay(d.date, new Date(Date.now() + 86_400_000)) ? t("tomorrow") : day(d.date, lang)}</Pill>
                  )}
                </li>
              );
            })}
            {w?.pm && (
              <li className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t("pm_signoff")}</p>
                  <p className="text-micro text-muted-foreground">{t("pm_detail", { pm: w.pm.name, n: w.pm.after_day })}</p>
                </div>
                <Pill tone="plain">{t("pm_after_all")}</Pill>
              </li>
            )}
          </ul>
        </Panel>

        <Panel title={t("photos")} right={<><span className="num">{report.photos.length}</span><PhotoButtons jobRef={jobRef} /></>}>
          {report.photos_error&&<p role="alert" className="text-sm text-destructive">{t("photos_load_failed")}</p>}
          <PhotoGrid photos={report.photos} cols={4} />
          <p className="mt-3 text-micro text-muted-foreground">{t("gallery_note")}</p>
        </Panel>

        {!!w?.rooms?.length && (
          <Panel title={t("scope_by_room")} bodyClassName="p-0">
            <ul className="divide-y text-sm">
              {w.rooms.map((r) => (
                <li key={r.name} className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2"><span>{r.name}</span><span className="num text-muted-foreground">{r.dim}</span><span className="num">{m2(r.area_m2)}</span></li>
              ))}
              <li className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2 font-medium"><span>{t("total")}</span><span /><span className="num">{m2(w.rooms.reduce((s, r) => s + r.area_m2, 0))}</span></li>
            </ul>
          </Panel>
        )}
      </div>
    </div>
  );
}
